import { useState, useEffect } from 'react';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  FileText,
  Building2,
  Save,
  Loader2,
  Plus,
  Trash2,
  Upload,
  Eye,
  Palette
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function FirmLetterheadManager() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  
  const [letterhead, setLetterhead] = useState({
    firm_name: '',
    address: {
      street: '',
      suite: '',
      city: '',
      state: '',
      zip_code: ''
    },
    phone: '',
    fax: '',
    email: '',
    website: '',
    logo_base64: '',
    logo_width: 150,
    tagline: '',
    attorneys: [],
    primary_color: '#1a365d',
    secondary_color: '#2d3748'
  });

  useEffect(() => {
    fetchLetterhead();
  }, [token]);

  const fetchLetterhead = async () => {
    try {
      const res = await fetch(`${API_URL}/api/case-management/firm-letterhead`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data) {
          setLetterhead({
            ...letterhead,
            ...data,
            address: data.address || letterhead.address,
            attorneys: data.attorneys || []
          });
        }
      }
    } catch (error) {
      console.error('Error fetching letterhead:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!letterhead.firm_name) {
      toast.error('Firm name is required');
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/case-management/firm-letterhead`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(letterhead)
      });

      if (res.ok) {
        toast.success('Letterhead saved successfully');
      } else {
        toast.error('Failed to save letterhead');
      }
    } catch (error) {
      toast.error('Failed to save letterhead');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 500000) { // 500KB limit
        toast.error('Logo file must be under 500KB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setLetterhead({ ...letterhead, logo_base64: reader.result });
        toast.success('Logo uploaded');
      };
      reader.readAsDataURL(file);
    }
  };

  const addAttorney = () => {
    setLetterhead({
      ...letterhead,
      attorneys: [...letterhead.attorneys, { name: '', title: '', bar_number: '', email: '', direct_phone: '' }]
    });
  };

  const removeAttorney = (index) => {
    setLetterhead({
      ...letterhead,
      attorneys: letterhead.attorneys.filter((_, i) => i !== index)
    });
  };

  const updateAttorney = (index, field, value) => {
    const newAttorneys = [...letterhead.attorneys];
    newAttorneys[index] = { ...newAttorneys[index], [field]: value };
    setLetterhead({ ...letterhead, attorneys: newAttorneys });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin mr-2" />
        <span>Loading letterhead...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <FileText className="w-5 h-5 text-purple-600" />
            Firm Letterhead
          </h2>
          <p className="text-sm text-muted-foreground">
            Customize your firm's letterhead for professional document exports
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowPreview(!showPreview)}>
            <Eye className="w-4 h-4 mr-2" />
            {showPreview ? 'Hide' : 'Show'} Preview
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save Letterhead
          </Button>
        </div>
      </div>

      <div className={`grid gap-6 ${showPreview ? 'grid-cols-2' : 'grid-cols-1'}`}>
        {/* Form */}
        <div className="space-y-6">
          {/* Basic Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                Firm Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Firm Name *</Label>
                <Input
                  placeholder="Smith & Associates Law Firm"
                  value={letterhead.firm_name}
                  onChange={(e) => setLetterhead({ ...letterhead, firm_name: e.target.value })}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Tagline</Label>
                <Input
                  placeholder="Fighting for Justice Since 1995"
                  value={letterhead.tagline}
                  onChange={(e) => setLetterhead({ ...letterhead, tagline: e.target.value })}
                />
              </div>

              {/* Logo Upload */}
              <div className="space-y-2">
                <Label>Logo</Label>
                <div className="flex items-center gap-4">
                  {letterhead.logo_base64 ? (
                    <div className="relative">
                      <img 
                        src={letterhead.logo_base64} 
                        alt="Firm logo" 
                        className="h-16 object-contain border rounded"
                      />
                      <button
                        onClick={() => setLetterhead({ ...letterhead, logo_base64: '' })}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <div className="w-24 h-16 border-2 border-dashed rounded flex items-center justify-center text-muted-foreground">
                      No logo
                    </div>
                  )}
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleLogoUpload}
                    />
                    <div className="flex items-center gap-2 px-3 py-2 border rounded hover:bg-slate-50">
                      <Upload className="w-4 h-4" />
                      Upload Logo
                    </div>
                  </label>
                  <div className="flex items-center gap-2">
                    <Label className="text-xs">Width:</Label>
                    <Input
                      type="number"
                      className="w-20 h-8"
                      value={letterhead.logo_width}
                      onChange={(e) => setLetterhead({ ...letterhead, logo_width: parseInt(e.target.value) || 150 })}
                    />
                    <span className="text-xs text-muted-foreground">px</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Address */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Address</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 space-y-2">
                  <Label>Street Address</Label>
                  <Input
                    placeholder="123 Legal Avenue"
                    value={letterhead.address.street}
                    onChange={(e) => setLetterhead({ 
                      ...letterhead, 
                      address: { ...letterhead.address, street: e.target.value }
                    })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Suite/Floor</Label>
                  <Input
                    placeholder="Suite 400"
                    value={letterhead.address.suite}
                    onChange={(e) => setLetterhead({ 
                      ...letterhead, 
                      address: { ...letterhead.address, suite: e.target.value }
                    })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>City</Label>
                  <Input
                    placeholder="Houston"
                    value={letterhead.address.city}
                    onChange={(e) => setLetterhead({ 
                      ...letterhead, 
                      address: { ...letterhead.address, city: e.target.value }
                    })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>State</Label>
                  <Input
                    placeholder="TX"
                    value={letterhead.address.state}
                    onChange={(e) => setLetterhead({ 
                      ...letterhead, 
                      address: { ...letterhead.address, state: e.target.value }
                    })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>ZIP Code</Label>
                  <Input
                    placeholder="77001"
                    value={letterhead.address.zip_code}
                    onChange={(e) => setLetterhead({ 
                      ...letterhead, 
                      address: { ...letterhead.address, zip_code: e.target.value }
                    })}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Contact Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input
                    placeholder="(713) 555-1234"
                    value={letterhead.phone}
                    onChange={(e) => setLetterhead({ ...letterhead, phone: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Fax</Label>
                  <Input
                    placeholder="(713) 555-1235"
                    value={letterhead.fax}
                    onChange={(e) => setLetterhead({ ...letterhead, fax: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    placeholder="contact@smithlaw.com"
                    value={letterhead.email}
                    onChange={(e) => setLetterhead({ ...letterhead, email: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Website</Label>
                  <Input
                    placeholder="www.smithlaw.com"
                    value={letterhead.website}
                    onChange={(e) => setLetterhead({ ...letterhead, website: e.target.value })}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Colors */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Palette className="w-4 h-4" />
                Brand Colors
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Primary Color</Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={letterhead.primary_color}
                      onChange={(e) => setLetterhead({ ...letterhead, primary_color: e.target.value })}
                      className="w-10 h-10 rounded cursor-pointer"
                    />
                    <Input
                      value={letterhead.primary_color}
                      onChange={(e) => setLetterhead({ ...letterhead, primary_color: e.target.value })}
                      className="flex-1"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Secondary Color</Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={letterhead.secondary_color}
                      onChange={(e) => setLetterhead({ ...letterhead, secondary_color: e.target.value })}
                      className="w-10 h-10 rounded cursor-pointer"
                    />
                    <Input
                      value={letterhead.secondary_color}
                      onChange={(e) => setLetterhead({ ...letterhead, secondary_color: e.target.value })}
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Attorneys */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Attorneys</CardTitle>
                <Button variant="outline" size="sm" onClick={addAttorney}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add Attorney
                </Button>
              </div>
              <CardDescription>
                Attorneys listed here will appear on the letterhead
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {letterhead.attorneys.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No attorneys added yet. Click "Add Attorney" to add one.
                </p>
              ) : (
                letterhead.attorneys.map((attorney, index) => (
                  <div key={index} className="p-4 border rounded-lg space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Attorney {index + 1}</span>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => removeAttorney(index)}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Name</Label>
                        <Input
                          placeholder="John Smith"
                          value={attorney.name}
                          onChange={(e) => updateAttorney(index, 'name', e.target.value)}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Title</Label>
                        <Input
                          placeholder="Partner"
                          value={attorney.title}
                          onChange={(e) => updateAttorney(index, 'title', e.target.value)}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Bar Number</Label>
                        <Input
                          placeholder="12345678"
                          value={attorney.bar_number}
                          onChange={(e) => updateAttorney(index, 'bar_number', e.target.value)}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Direct Phone</Label>
                        <Input
                          placeholder="(713) 555-1236"
                          value={attorney.direct_phone}
                          onChange={(e) => updateAttorney(index, 'direct_phone', e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>

        {/* Preview */}
        {showPreview && (
          <div className="sticky top-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Letterhead Preview</CardTitle>
              </CardHeader>
              <CardContent>
                <div 
                  className="border rounded-lg p-6 bg-white min-h-[400px]"
                  style={{ fontFamily: 'serif' }}
                >
                  {/* Logo */}
                  {letterhead.logo_base64 && (
                    <div className="flex justify-center mb-4">
                      <img 
                        src={letterhead.logo_base64} 
                        alt="Logo" 
                        style={{ width: letterhead.logo_width, objectFit: 'contain' }}
                      />
                    </div>
                  )}
                  
                  {/* Firm Name */}
                  <h1 
                    className="text-2xl font-bold text-center mb-1"
                    style={{ color: letterhead.primary_color }}
                  >
                    {letterhead.firm_name || 'Your Firm Name'}
                  </h1>
                  
                  {/* Tagline */}
                  {letterhead.tagline && (
                    <p 
                      className="text-sm italic text-center mb-2"
                      style={{ color: letterhead.secondary_color }}
                    >
                      {letterhead.tagline}
                    </p>
                  )}
                  
                  {/* Address */}
                  <p 
                    className="text-xs text-center"
                    style={{ color: letterhead.secondary_color }}
                  >
                    {[
                      letterhead.address.street,
                      letterhead.address.suite,
                      `${letterhead.address.city}, ${letterhead.address.state} ${letterhead.address.zip_code}`
                    ].filter(Boolean).join(' | ')}
                  </p>
                  
                  {/* Contact */}
                  <p 
                    className="text-xs text-center mt-1"
                    style={{ color: letterhead.secondary_color }}
                  >
                    {[
                      letterhead.phone && `Tel: ${letterhead.phone}`,
                      letterhead.fax && `Fax: ${letterhead.fax}`,
                      letterhead.email,
                      letterhead.website
                    ].filter(Boolean).join(' | ')}
                  </p>
                  
                  {/* Attorneys */}
                  {letterhead.attorneys.length > 0 && (
                    <p 
                      className="text-xs text-center mt-1"
                      style={{ color: letterhead.secondary_color }}
                    >
                      {letterhead.attorneys.map(a => 
                        a.bar_number ? `${a.name} (Bar #${a.bar_number})` : a.name
                      ).join(' • ')}
                    </p>
                  )}
                  
                  {/* Divider */}
                  <hr 
                    className="my-4"
                    style={{ borderColor: letterhead.primary_color, borderWidth: 2 }}
                  />
                  
                  {/* Sample Content */}
                  <div className="text-xs text-gray-400 text-center mt-8">
                    Document content will appear here
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
